import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrowserSearch } from '@/hooks/useBrowserSearch';
import { useTranslation } from '@/i18n/i18n';
import { buildSignUpUrl, resolveSignupEntityType, type SignupEntityType } from '@/lib/auth-url';

export function useSignupEntityType(options: { entityType?: SignupEntityType } = {}) {
  const { pathname } = useLocation();
  const search = useBrowserSearch();

  return resolveSignupEntityType(pathname, search ?? '', options);
}

/**
 * Returns a signup URL with UTM parameters forwarded from the current page.
 * Memoized to avoid recalculating on every render. While prerendered markup
 * hydrates it is the unattributed URL the build rendered; the query and
 * first-touch attribution join on the next render (useBrowserSearch).
 */
export function useSignupUrl(
  options: {
    entityType?: SignupEntityType;
    source?: string;
    placement?: string;
    page?: string;
  } = {}
) {
  const { locale } = useTranslation();
  const { pathname } = useLocation();
  const search = useBrowserSearch();
  const entityType = resolveSignupEntityType(pathname, search ?? '', options);

  return useMemo(
    () =>
      buildSignUpUrl(locale, search, {
        entityType,
        source: options.source,
        placement: options.placement,
        page: options.page,
      }),
    [entityType, locale, search, options.source, options.placement, options.page]
  );
}
